import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { formatDateTime } from "@bw/shared/utils";
import { PlatformPage } from "@/components/PlatformPage";
import { StatusBadge } from "@/components/StatusBadge";
import { listOnboardingOrgs, listReports } from "@/shared/api";
import { orgBusinessName } from "@/shared/orgDisplay";
import { getActivityFeed, getDashboardStats } from "../api";

const QUEUE_PREVIEW_LIMIT = 4;

function humanizeAction(action: string): string {
  const normalized = action.trim().replace(/_/g, " ");
  if (!normalized) return action;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

interface QueueCardProps {
  title: string;
  count: number | null;
  isLoading: boolean;
  isError: boolean;
  emptyLabel: string;
  viewAllTo: string;
  children: ReactNode;
  isEmpty: boolean;
}

function QueueCard({
  title,
  count,
  isLoading,
  isError,
  isEmpty,
  emptyLabel,
  viewAllTo,
  children,
}: QueueCardProps) {
  let body: ReactNode;
  if (isError) {
    body = (
      <Alert severity="error" role="alert">
        Couldn’t load {title.toLowerCase()}. Try again shortly.
      </Alert>
    );
  } else if (isLoading) {
    body = (
      <Box
        role="status"
        aria-label={`Loading ${title}`}
        sx={{ display: "flex", justifyContent: "center", py: 4 }}
      >
        <CircularProgress size={30} />
      </Box>
    );
  } else if (isEmpty) {
    body = (
      <Typography color="text.secondary" variant="body2" sx={{ py: 3 }}>
        {emptyLabel}
      </Typography>
    );
  } else {
    body = <List disablePadding>{children}</List>;
  }

  return (
    <Card
      component="section"
      role="region"
      aria-label={title}
      variant="outlined"
      sx={{
        display: "flex",
        minHeight: { xs: "auto", sm: 280 },
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
          px: 2.5,
          py: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", minWidth: 0 }}
        >
          <Typography
            component="h2"
            variant="subtitle1"
            sx={{ fontWeight: 700 }}
          >
            {title}
          </Typography>
          {!isError && count !== null && (
            <Chip
              size="small"
              label={count.toLocaleString("en-US")}
              aria-label={`${count} items`}
            />
          )}
        </Stack>
        <Button
          component={RouterLink}
          to={viewAllTo}
          size="small"
          sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
        >
          View all →
        </Button>
      </Stack>
      <Box sx={{ flexGrow: 1, px: 2.5, py: 1 }}>{body}</Box>
    </Card>
  );
}

function QueueRow({
  primary,
  secondary,
  status,
  timestamp,
}: {
  primary: string;
  secondary?: string;
  status?: string;
  timestamp?: string;
}) {
  return (
    <ListItem
      disableGutters
      divider
      sx={{
        alignItems: { xs: "stretch", sm: "flex-start" },
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        gap: { xs: 0.75, sm: 2 },
        py: 1.5,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: { xs: "normal", sm: "nowrap" },
          }}
        >
          {primary}
        </Typography>
        {secondary && (
          <Typography
            color="text.secondary"
            variant="caption"
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: { xs: "normal", sm: "nowrap" },
            }}
          >
            {secondary}
          </Typography>
        )}
      </Box>
      <Stack
        direction={{ xs: "row", sm: "column" }}
        spacing={0.75}
        sx={{
          width: { xs: "100%", sm: "auto" },
          flexShrink: 0,
          alignItems: { xs: "center", sm: "flex-end" },
          justifyContent: { xs: "space-between", sm: "flex-start" },
        }}
      >
        {status && <StatusBadge status={status} />}
        {timestamp && (
          <Typography
            color="text.secondary"
            variant="caption"
            sx={{ whiteSpace: "nowrap" }}
          >
            {formatDateTime(timestamp)}
          </Typography>
        )}
      </Stack>
    </ListItem>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography color="text.secondary" variant="overline">
          {label}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

export function PlatformDashboardPage() {
  const statsQuery = useQuery({
    queryKey: ["platform", "dashboard-stats"],
    queryFn: getDashboardStats,
  });

  const approvalsQuery = useQuery({
    queryKey: ["platform", "dashboard", "pending-approvals"],
    queryFn: listOnboardingOrgs,
  });

  const reportsQuery = useQuery({
    queryKey: ["platform", "dashboard", "open-reports"],
    queryFn: () =>
      listReports({ status: "pending", limit: QUEUE_PREVIEW_LIMIT }),
  });

  const activityQuery = useQuery({
    queryKey: ["platform", "dashboard", "recent-activity"],
    queryFn: () => getActivityFeed(QUEUE_PREVIEW_LIMIT),
  });

  const stats = statsQuery.data;
  const pendingOrgs = approvalsQuery.data?.pending ?? [];
  const pendingPreview = pendingOrgs.slice(0, QUEUE_PREVIEW_LIMIT);
  const reports = reportsQuery.data?.items ?? [];
  const reportsTotal = reportsQuery.data?.total ?? reports.length;
  const reportsPreview = reports.slice(0, QUEUE_PREVIEW_LIMIT);
  const activity = activityQuery.data ?? [];
  const activityPreview = activity.slice(0, QUEUE_PREVIEW_LIMIT);

  return (
    <PlatformPage title="Dashboard">
      <Stack spacing={3}>
        <Box
          component="section"
          role="region"
          aria-label="Platform statistics"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(4, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          {statsQuery.isError ? (
            <Alert severity="error" role="alert" sx={{ gridColumn: "1 / -1" }}>
              Couldn’t load platform statistics. Try again shortly.
            </Alert>
          ) : statsQuery.isLoading || !stats ? (
            <Card variant="outlined" sx={{ gridColumn: "1 / -1" }}>
              <Box
                role="status"
                aria-label="Loading platform statistics"
                sx={{ display: "flex", justifyContent: "center", py: 5 }}
              >
                <CircularProgress size={32} />
              </Box>
            </Card>
          ) : (
            <>
              <MetricCard
                label="Organizations"
                value={stats.total_organizations.toLocaleString("en-US")}
              />
              <MetricCard
                label="Pending approvals"
                value={stats.pending_approvals.toLocaleString("en-US")}
              />
              <MetricCard
                label="Total users"
                value={stats.total_users.toLocaleString("en-US")}
              />
              <MetricCard
                label="Facilities"
                value={stats.total_facilities.toLocaleString("en-US")}
              />
            </>
          )}
        </Box>

        <Box
          component="section"
          aria-label="Work queues"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(3, minmax(0, 1fr))",
            },
            gap: 2.5,
          }}
        >
          <QueueCard
            title="Pending approvals"
            count={approvalsQuery.isError ? null : pendingOrgs.length}
            isLoading={approvalsQuery.isLoading}
            isError={approvalsQuery.isError}
            isEmpty={!approvalsQuery.isLoading && pendingOrgs.length === 0}
            emptyLabel="No pending approvals."
            viewAllTo="/approvals"
          >
            {pendingPreview.map((org) => (
              <QueueRow
                key={org.org_id}
                primary={orgBusinessName(org)}
                secondary={org.owner_email ?? org.owner_full_name ?? undefined}
                status={org.status}
                timestamp={org.created_at}
              />
            ))}
          </QueueCard>

          <QueueCard
            title="Open moderation reports"
            count={reportsQuery.isError ? null : reportsTotal}
            isLoading={reportsQuery.isLoading}
            isError={reportsQuery.isError}
            isEmpty={!reportsQuery.isLoading && reports.length === 0}
            emptyLabel="No open reports."
            viewAllTo="/moderation"
          >
            {reportsPreview.map((report) => (
              <QueueRow
                key={report.report_id}
                primary={report.reason}
                secondary={`${report.target_type} · ${report.target_id}`}
                status={report.status}
                timestamp={report.created_at}
              />
            ))}
          </QueueCard>

          <QueueCard
            title="Recent activity"
            count={null}
            isLoading={activityQuery.isLoading}
            isError={activityQuery.isError}
            isEmpty={!activityQuery.isLoading && activity.length === 0}
            emptyLabel="No recent activity."
            viewAllTo="/audit"
          >
            {activityPreview.map((entry) => (
              <QueueRow
                key={entry.entry_id}
                primary={humanizeAction(entry.action)}
                secondary={`${entry.resource_type} · ${entry.resource_id}`}
                timestamp={entry.created_at}
              />
            ))}
          </QueueCard>
        </Box>
      </Stack>
    </PlatformPage>
  );
}
